/**
 * ================================================================
 * LP Global Product Card — JavaScript
 * ================================================================
 * Handles variant swatch switching, picture source updates,
 * price updates, Add to Cart, Buy Now, and Favorite toggle.
 * ================================================================
 */
(function () {
  'use strict';

  /**
   * Initialize a single product card instance.
   */
  function initCard(cardEl) {
    if (cardEl.dataset.lpgpcInitialized) return;
    cardEl.dataset.lpgpcInitialized = 'true';

    /**
     * Get the correct money format for this card.
     * Reads data-currency-code-enabled to decide whether to use currency format.
     * Falls back to Shopify global currency if data attributes are empty.
     */
    function getMoneyFormat() {
      var currencyCodeEnabled = cardEl.dataset.currencyCodeEnabled === 'true';
      if (currencyCodeEnabled) {
        var withCurrency = cardEl.dataset.moneyWithCurrencyFormat;
        if (withCurrency) return withCurrency;
      }
      var plain = cardEl.dataset.moneyFormat;
      if (plain) return plain;
      // Fallback: try theme variables
      if (typeof window.themeVariables !== 'undefined' && window.themeVariables.moneyFormat) {
        return window.themeVariables.moneyFormat;
      }
      // Last resort: construct from Shopify currency global
      if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        var active = Shopify.currency.active;
        if (currencyCodeEnabled) {
          return '${{amount}} ' + active;
        }
        return '${{amount}}';
      }
      return '${{amount}}';
    }

    /**
     * Format money value (in cents) to display string.
     * Ensures currency code is preserved when currency_code_enabled is on.
     */
    function formatMoneyValue(cents) {
      var formatted;
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        formatted = Shopify.formatMoney(cents, getMoneyFormat());
      } else {
        formatted = '$' + (cents / 100).toFixed(2);
      }
      // Safety net: if currency code is enabled but not present in the output, append it
      var currencyCodeEnabled = cardEl.dataset.currencyCodeEnabled === 'true';
      if (currencyCodeEnabled && typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        var code = ' ' + Shopify.currency.active;
        if (formatted.indexOf(code) === -1) {
          formatted = formatted + code;
        }
      }
      return formatted;
    }

    var productHandle = cardEl.dataset.productHandle;
    var hasColorOption = cardEl.dataset.hasColorOption === 'true';
    var selectedVariantId = cardEl.dataset.selectedVariantId;

    // Parse variant images JSON from data attribute
    var variantImages = {};
    try {
      variantImages = JSON.parse(cardEl.getAttribute('data-variant-images') || '{}');
    } catch (e) {
      variantImages = {};
    }

    // DOM references
    var pictureEl = cardEl.querySelector('[data-product-picture]');
    var sourceDesktop = cardEl.querySelector('[data-source-desktop]');
    var sourceSmallDesktop = cardEl.querySelector('[data-source-small-desktop]');
    var sourceTablet = cardEl.querySelector('[data-source-tablet]');
    var sourceMobile = cardEl.querySelector('[data-source-mobile]');
    var imgEl = cardEl.querySelector('[data-product-img]');
    var swatchesContainer = cardEl.querySelector('[data-variant-swatches]');
    var priceEl = cardEl.querySelector('[data-price]');
    var comparePriceEl = cardEl.querySelector('[data-compare-price]');
    var soldOutBtn = cardEl.querySelector('[data-sold-out-btn]');
    var activeBtns = cardEl.querySelector('[data-active-btns]');
    var addToCartBtns = cardEl.querySelectorAll('[data-add-to-cart]');
    var buyNowLinks = cardEl.querySelectorAll('[data-buy-now]');
    var heartBtn = cardEl.querySelector('[data-favorite-toggle]');

    // Build variant data map from swatch labels
    var variantDataMap = {};
    if (swatchesContainer) {
      var swatchInputs = swatchesContainer.querySelectorAll('[data-swatch-input]');
      swatchInputs.forEach(function (input) {
        var label = swatchesContainer.querySelector('label[for="' + input.id + '"]');
        if (!label) return;
        variantDataMap[input.dataset.variantId] = {
          id: input.dataset.variantId,
          available: label.dataset.variantAvailable !== 'false',
          price: parseInt(label.dataset.variantPrice, 10) || 0,
          comparePrice: parseInt(label.dataset.variantComparePrice, 10) || 0,
          imageId: label.dataset.variantImageId || '',
          value: input.value
        };
      });
    }

    /**
     * Get selected swatch element.
     */
    function getSelectedSwatch() {
      if (!swatchesContainer) return null;
      return swatchesContainer.querySelector('.lpgpc-swatch--selected');
    }

    /**
     * Update the picture element sources for a given variant ID.
     */
    function updateProductImage(variantId) {
      var imgData = variantImages[variantId];
      if (!imgData) return;

      if (sourceDesktop && imgData['210']) {
        sourceDesktop.srcset = imgData['210'];
      }
      if (sourceSmallDesktop && imgData['163']) {
        sourceSmallDesktop.srcset = imgData['163'];
      }
      if (sourceTablet && imgData['110']) {
        sourceTablet.srcset = imgData['110'];
      }
      if (sourceMobile && imgData['110']) {
        sourceMobile.srcset = imgData['110'];
      }
      if (imgEl) {
        imgEl.src = imgData['fallback'] || imgData['210'];
        imgEl.alt = imgData['alt'] || '';
      }
    }

    /**
     * Update price display.
     */
    function updatePricing(variantData) {
      if (!priceEl) return;

      priceEl.textContent = formatMoneyValue(variantData.price);

      if (comparePriceEl) {
        if (variantData.comparePrice > 0 && variantData.comparePrice > variantData.price) {
          comparePriceEl.classList.remove('lpgpc-hidden');
          comparePriceEl.textContent = formatMoneyValue(variantData.comparePrice);
        } else {
          comparePriceEl.classList.add('lpgpc-hidden');
        }
      }
    }

    /**
     * Update button states based on variant availability.
     */
    function updateButtonStates(variantData) {
      var isAvailable = variantData ? variantData.available : true;
      var variantId = variantData ? variantData.id : '';

      if (isAvailable) {
        if (soldOutBtn) soldOutBtn.classList.add('lpgpc-hidden');
        if (activeBtns) activeBtns.classList.remove('lpgpc-hidden');
      } else {
        if (soldOutBtn) soldOutBtn.classList.remove('lpgpc-hidden');
        if (activeBtns) activeBtns.classList.add('lpgpc-hidden');
      }

      // Update Add to Cart variant IDs
      addToCartBtns.forEach(function (btn) {
        btn.dataset.variantId = variantId;
      });

      // Update Buy Now hrefs
      buyNowLinks.forEach(function (link) {
        if (variantId) {
          link.href = '/cart/' + variantId + ':1?checkout';
        }
        link.dataset.variantId = variantId;
      });
    }

    /**
     * Handle variant selection.
     */
    function selectVariant(variantId) {
      var variantData = variantDataMap[variantId];
      if (!variantData) return;

      // Update card state
      cardEl.dataset.selectedVariantId = variantId;

      // Update image if variant has one
      if (variantImages[variantId]) {
        updateProductImage(variantId);
      }

      // Update pricing
      updatePricing(variantData);

      // Update button states
      updateButtonStates(variantData);

      // Dispatch custom event
      cardEl.dispatchEvent(
        new CustomEvent('lpgpc:variant-changed', {
          bubbles: true,
          detail: {
            productHandle: productHandle,
            variantId: variantId,
            variantData: variantData
          }
        })
      );
    }

    /**
     * Update swatch UI selection state.
     */
    function updateSwatchUI(variantId) {
      if (!swatchesContainer) return;

      // Update all label states
      swatchesContainer.querySelectorAll('.lpgpc-swatch').forEach(function (label) {
        var isTarget = label.dataset.variantId === variantId;
        label.classList.toggle('lpgpc-swatch--selected', isTarget);
        label.setAttribute('aria-checked', String(isTarget));
        label.setAttribute('tabindex', isTarget ? '0' : '-1');
      });

      // Update hidden inputs
      swatchesContainer.querySelectorAll('[data-swatch-input]').forEach(function (input) {
        input.checked = (input.dataset.variantId === variantId);
      });
    }

    /**
     * Wire up swatch events.
     */
    function initSwatches() {
      if (!swatchesContainer) return;

      // Keyboard navigation
      swatchesContainer.addEventListener('keydown', function (e) {
        var allSwatches = Array.from(
          swatchesContainer.querySelectorAll('[data-variant-id]')
        );
        var currentIdx = allSwatches.indexOf(document.activeElement);
        if (currentIdx === -1) return;

        var nextIdx;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          nextIdx = (currentIdx + 1) % allSwatches.length;
          allSwatches[nextIdx].focus();
          allSwatches[nextIdx].click();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          nextIdx = (currentIdx - 1 + allSwatches.length) % allSwatches.length;
          allSwatches[nextIdx].focus();
          allSwatches[nextIdx].click();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (document.activeElement && document.activeElement.dataset.variantId) {
            document.activeElement.click();
          }
        }
      });

      // Click events on labels
      swatchesContainer.addEventListener('click', function (e) {
        var label = e.target.closest('[data-variant-id]');
        if (!label) return;
        if (label.classList.contains('lpgpc-swatch--selected')) return;
        if (label.classList.contains('lpgpc-swatch--sold-out')) return;

        var variantId = label.dataset.variantId;
        if (!variantId) return;

        updateSwatchUI(variantId);
        selectVariant(variantId);

        // Also update the hidden radio input
        var input = swatchesContainer.querySelector(
          '[data-swatch-input][data-variant-id="' + variantId + '"]'
        );
        if (input) input.checked = true;
      });
    }

    /**
     * Handle Add to Cart via Shopify AJAX API.
     */
    function initAddToCart() {
      addToCartBtns.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (btn.classList.contains('lpgpc-btn--loading')) return;

          var variantId = btn.dataset.variantId || selectedVariantId;
          if (!variantId) return;

          // Show loading
          btn.classList.add('lpgpc-btn--loading');
          btn.disabled = true;
          var originalText = btn.textContent;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId, 10), quantity: 1 }]
            })
          })
            .then(function (resp) {
              if (!resp.ok) throw new Error('Failed');
              return resp.json();
            })
            .then(function () {
              // Trigger cart refresh globally
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:refresh', { bubbles: true })
              );
              // Show success
              btn.textContent = '✓ Added';
              setTimeout(function () {
                btn.textContent = originalText;
                btn.disabled = false;
                btn.classList.remove('lpgpc-btn--loading');
              }, 2000);
            })
            .catch(function () {
              btn.classList.remove('lpgpc-btn--loading');
              btn.disabled = false;
            });
        });
      });
    }

    /**
     * Favorite toggle.
     */
    function initFavorite() {
      if (!heartBtn) return;

      heartBtn.addEventListener('click', function () {
        var isActive = heartBtn.classList.toggle('lpgpc-heart--active');
        heartBtn.setAttribute('aria-pressed', String(isActive));
        heartBtn.setAttribute(
          'aria-label',
          isActive ? 'Remove from wishlist' : 'Add to wishlist'
        );

        cardEl.dispatchEvent(
          new CustomEvent('lpgpc:favorite-toggled', {
            bubbles: true,
            detail: { productHandle: productHandle, isFavorited: isActive }
          })
        );
      });
    }

    // Initialize
    initSwatches();
    initAddToCart();
    initFavorite();
  }

  /**
   * Initialize all cards on the page.
   */
  function initAllCards() {
    var cards = document.querySelectorAll('.lpgpc-card');
    cards.forEach(function (card) {
      initCard(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCards);
  } else {
    initAllCards();
  }

  // Re-init on Shopify section load/render
  document.addEventListener('shopify:section:load', initAllCards);
})();
